import { useEffect } from 'react';
import { X } from 'lucide-react';

import type { Rock } from '@/api/eos/rocks.api';
import { CommentThread } from '@/components/comms/CommentThread';
import { RockApprovalSection } from '@/components/eos/RockApprovalSection';
import { RockDependencies } from '@/components/eos/RockDependencies';
import { RockMilestones } from '@/components/eos/RockMilestones';
import { RockStatusBadge } from '@/components/eos/RockStatusBadge';
import { FileAttachments } from '@/components/files/FileAttachments';

/**
 * Right-side slide-over for a single rock (Phase H — Rocks depth + workflow).
 * Houses the approval lifecycle, milestones (due-date editor + health toggle),
 * prerequisite dependencies (with a Blocked banner), file attachments, and the
 * discussion thread. Mirrors the to-do drawer shell — Esc to close, backdrop
 * dismiss, and every mutation inside invalidates `['eos','rocks']` so the
 * parent re-derives the open rock from fresh list data by id.
 */
export function RockDetailDrawer({
  rock,
  allRocks,
  canEdit,
  onClose,
}: {
  rock: Rock;
  allRocks: Rock[];
  canEdit: boolean;
  onClose: () => void;
}) {
  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-40 flex">
      <button
        type="button"
        aria-label="Close drawer"
        onClick={onClose}
        className="flex-1 animate-fade-in bg-black/30"
      />
      <aside
        role="dialog"
        aria-label={`Rock: ${rock.title}`}
        className="ml-auto flex h-full w-full max-w-md animate-slide-up flex-col overflow-y-auto bg-white shadow-kw-lg sm:animate-fade-in"
      >
        <header className="flex items-start justify-between gap-3 border-b border-kw-border p-5">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h2 className="typo-h3">Rock</h2>
              <RockStatusBadge status={rock.status} />
            </div>
            <p className="mt-1 text-[14px] font-medium text-kw-shark">{rock.title}</p>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="rounded p-1 text-kw-muted hover:bg-kw-bg hover:text-kw-shark"
          >
            <X className="size-5" />
          </button>
        </header>

        <div className="flex-1 space-y-6 p-5">
          <section>
            <RockApprovalSection rock={rock} canEdit={canEdit} />
          </section>

          <section className="border-t border-kw-border pt-5">
            <RockMilestones rockId={rock.id} milestones={rock.milestones} canEdit={canEdit} />
          </section>

          <section className="border-t border-kw-border pt-5">
            <RockDependencies rock={rock} allRocks={allRocks} canEdit={canEdit} />
          </section>

          <section className="border-t border-kw-border pt-5">
            <h3 className="mb-3 text-[12px] font-medium uppercase tracking-wide text-kw-muted">
              Attachments
            </h3>
            <FileAttachments entityType="eos_rock" entityId={rock.id} />
          </section>

          <section className="border-t border-kw-border pt-5">
            <h3 className="mb-3 text-[12px] font-medium uppercase tracking-wide text-kw-muted">Discussion</h3>
            <CommentThread entityType="rock" entityId={rock.id} />
          </section>
        </div>
      </aside>
    </div>
  );
}
